import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import axiosInstance from "../../api/axiosInstance.js";
import { notify, Toast } from "../../common/Toast/index.js";

const CalendarComponent = () => {
  const [currentEvents, setCurrentEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    Subject: "",
    Followup_Date: "",
    Followup_Time: "00:00:00",
    Description: "",
  });
  const [selectedEventId, setSelectedEventId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get("Followup/GetAllFollowups");
      const events = response.data;

      const formattedEvents = events
        .map((event) => {
          const datePart = event.followup_Date.split("T")[0];
          const timePart = event.followup_Time?.includes(":")
            ? event.followup_Time
            : "00:00:00";

          if (!datePart || !event.subject) {
            console.warn("⚠️ Skipping invalid event:", event);
            return null;
          }

          const startDateTime = `${datePart}T${timePart}`;

          return {
            id: event.followup_Id,
            title: event.subject,
            start: startDateTime,
            end: startDateTime,
            allDay: false,
          };
        })
        .filter(Boolean);

      // notify("✅ Events passed to calendar:", formattedEvents, "success");
      setCurrentEvents(formattedEvents);
    } catch (error) {
      console.error("❌ Error fetching events:", error);
    }
  };

  const handleDateClick = (selected) => {
    const isoDate = selected.startStr.split("T")[0];

    setFormData({
      Subject: "",
      Followup_Date: isoDate,
      Followup_Time: "00:00:00",
      Description: "",
    });
    setSelectedEventId(null);
    setIsModalOpen(true);
  };

  const handleEventClick = async (selected) => {
    try {
      const response = await axiosInstance.get(
        `Followup/GetFollowupById/${selected.event.id}`
      );

      const data = response.data;
      const formattedDate = data.followup_Date
        ? data.followup_Date.split("T")[0]
        : "";

      setFormData({
        Subject: data.subject || "",
        Followup_Date: formattedDate,
        Followup_Time: data.followup_Time || "00:00:00",
        Description: data.description || "",
      });

      setSelectedEventId(data.followup_Id);
      setIsModalOpen(true);
    } catch (error) {
      console.error("❌ Error fetching event details:", error);
    }
  };

  const handleEventDrop = async (info) => {
    const { event } = info;
    const followupId = event.id;
    const newDate = event.start;
    const localDate = new Date(
      newDate.getTime() - newDate.getTimezoneOffset() * 60000
    );
    const formattedDate = localDate.toISOString().split("T")[0];
    const formattedTime = localDate.toTimeString().slice(0, 5) + ":00";

    try {
      await axiosInstance.post("Followup/RescheduleFollowup", {
        Followup_Id: followupId,
        Followup_Date: formattedDate,
        Followup_Time: formattedTime,
      });

      notify(`✅ Event moved to ${formattedDate} ${formattedTime}`, "success");

      fetchEvents();
    } catch (error) {
      console.error("❌ Reschedule failed:", error);
      notify("❌ Could not reschedule event.", "error");
      info.revert();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      Subject: "",
      Followup_Date: "",
      Followup_Time: "00:00:00",
      Description: "",
    });
    setSelectedEventId(null);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        Subject: formData.Subject,
        Followup_Date: formData.Followup_Date,
        Followup_Time: formData.Followup_Time || "00:00:00",
        Description: formData.Description,
      };

      if (selectedEventId) {
        await axiosInstance.post("Followup/SaveUpdate", {
          Followup_Id: selectedEventId,
          ...eventData,
          Is_Complete: true,
          Is_Reschedule: false,
        });
      } else {
        await axiosInstance.post("Followup/SaveUpdate", {
          ...eventData,
          Is_Complete: false,
          Is_Reschedule: false,
        });
      }
      handleCloseModal();
      notify("Event Add Successfully", "success");
      setTimeout(fetchEvents, 300);
    } catch (error) {
      console.error("❌ Error saving event:", error);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      if (selectedEventId) {
        await axiosInstance.delete(`Followup/Delete/${selectedEventId}`);
        fetchEvents();
      }
      handleCloseModal();
      notify("Event Delete Successfully", "error");
    } catch (error) {
      console.error("❌ Error deleting event:", error);
    }
  };

  return (
    <>
      <Toast />
      <div className="flex flex-col items-center p-10 rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5 ">
        <div className="w-full ">
          <FullCalendar
            height="85vh"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={{
              left: "prev,next",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            initialView="dayGridMonth"
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            select={handleDateClick}
            eventClick={handleEventClick}
            events={currentEvents}
            eventDrop={handleEventDrop}
          />
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
              <h2 className="text-xl font-bold mb-4">
                {selectedEventId ? "Edit Event" : "Add New Event"}
              </h2>
              <form onSubmit={handleSaveEvent}>
                <input
                  type="text"
                  placeholder="Subject"
                  name="Subject"
                  value={formData.Subject}
                  onChange={(e) =>
                    setFormData({ ...formData, Subject: e.target.value })
                  }
                  required
                  className="border border-gray-300 p-2 w-full rounded mb-4"
                />
                <input
                  type="date"
                  name="Followup_Date"
                  value={formData.Followup_Date}
                  readOnly={!selectedEventId}
                  onChange={(e) =>
                    setFormData({ ...formData, Followup_Date: e.target.value })
                  }
                  required
                  className={`border border-gray-300 p-2 w-full rounded mb-4 ${
                    !selectedEventId ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
                <input
                  type="time"
                  name="Followup_Time"
                  value={formData.Followup_Time.replace(":00", "")}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Followup_Time: e.target.value + ":00",
                    })
                  }
                  required
                  className="border border-gray-300 p-2 w-full rounded mb-4"
                />
                <textarea
                  name="Description"
                  placeholder="Description"
                  value={formData.Description}
                  onChange={(e) =>
                    setFormData({ ...formData, Description: e.target.value })
                  }
                  className="border border-gray-300 p-2 w-full rounded mb-4"
                />
                <div className="flex justify-end gap-2">
                  {selectedEventId && (
                    <button
                      type="button"
                      className="bg-danger text-white px-4 py-2 rounded"
                      onClick={handleDeleteEvent}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    className="bg-gray-6 text-white px-4 py-2 rounded"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-secondary text-white px-4 py-2 rounded"
                  >
                    {selectedEventId ? "Update Event" : "Add Event"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CalendarComponent;
